import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  animationFrames,
  endWith,
  filter,
  map,
  pairwise,
  startWith,
  switchMap,
  takeWhile,
} from 'rxjs';

import { Easing } from '@tweenjs/tween.js';

const EasingMapping = {
  linear: Easing.Linear.None,
  quadraticIn: Easing.Quadratic.In,
  quadraticOut: Easing.Quadratic.Out,
  quadraticInOut: Easing.Quadratic.InOut,
  cubicIn: Easing.Cubic.In,
  cubicOut: Easing.Cubic.InOut,
  cubicInOut: Easing.Cubic.InOut,
  quarticIn: Easing.Quartic.In,
  quarticOut: Easing.Quartic.Out,
  quarticInOut: Easing.Quartic.InOut,
  quinticIn: Easing.Quintic.In,
  quinticOut: Easing.Quintic.Out,
  quinticInOut: Easing.Quintic.InOut,
  sinusoidalIn: Easing.Sinusoidal.In,
  sinusoidalOut: Easing.Sinusoidal.Out,
  sinusoidalInOut: Easing.Sinusoidal.InOut,
  exponentialIn: Easing.Exponential.In,
  exponentialOut: Easing.Exponential.Out,
  exponentialInOut: Easing.Exponential.InOut,
  circularIn: Easing.Circular.In,
  circularOut: Easing.Circular.Out,
  circularInOut: Easing.Circular.InOut,
  elasticIn: Easing.Elastic.In,
  elasticOut: Easing.Elastic.Out,
  elasticInOut: Easing.Elastic.InOut,
  backIn: Easing.Back.In,
  backOut: Easing.Back.Out,
  backInOut: Easing.Back.InOut,
  bounceIn: Easing.Bounce.In,
  bounceOut: Easing.Bounce.Out,
  bounceInOut: Easing.Bounce.InOut,
};

type EasingKey = keyof typeof EasingMapping;

@Component({
  selector: 'app-counter',
  imports: [DecimalPipe],
  templateUrl: './counter.component.html',
  styleUrl: './counter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CounterComponent {
  public readonly target = input(0);

  public readonly duration = input(0);

  public readonly easing = input<EasingKey>('linear');

  private readonly _animationFrames$ = animationFrames().pipe(
    map(({ elapsed }) => elapsed / this.duration()),
    takeWhile((value) => value <= 1),
    endWith(1)
  );

  private readonly _tween$ = toObservable(this.target).pipe(
    startWith(0),
    pairwise(),
    filter(([previous, current]) => previous !== current),
    switchMap(([previous, current]) =>
      this._animationFrames$.pipe(
        map(this._easingFunc()),

        map((value) => (current - previous) * value),

        map((value) => previous + value)
      )
    )
  );

  private readonly _easingFunc = computed(() => EasingMapping[this.easing()]);

  protected readonly count = toSignal(this._tween$, { initialValue: 0 });
}
